import api from "src/shared/api";
import { EQueryTagTypes } from "src/shared/types/api";
import { getProvidesTags } from "src/shared/utils/url/query.helpers";
import {
  IAccountLvlSettingsQuery,
  IAccountLvlSettingsResponse,
  IAppLvlItem,
  IServiceLvlItem,
} from "../types/types";

const serviceToolsClientApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAppLvlSettings: build.query<
      { data: IAppLvlItem[] },
      { client_id?: number | string }
    >({
      query: ({ client_id }) => ({
        url: `api/service-tools/clients/${client_id}/settings`,
        method: "GET",
      }),
      providesTags: (response, _err, { client_id }) => {
        const base = [
          {
            type: EQueryTagTypes.ST_APP_SETTINGS as const,
            id: `${client_id}:LIST`,
          },
        ];
        if (!response) return base;

        return [
          ...base,
          ...getProvidesTags<IAppLvlItem>(
            response.data,
            EQueryTagTypes.ST_APP_SETTINGS,
          ),
        ];
      },
    }),
    getServiceLvlSettings: build.query<
      { data: IServiceLvlItem[] },
      { client_id?: number | string; service: string }
    >({
      query: ({ client_id, service }) => ({
        url: `api/service-tools/clients/${client_id}/advertise-services/${service}/settings`,
        method: "GET",
      }),
      providesTags: (response, _err, { client_id, service }) => {
        const base = [
          {
            type: EQueryTagTypes.ST_SERVICE_SETTINGS as const,
            id: `${client_id}:${service}:LIST`,
          },
          {
            type: EQueryTagTypes.ST_SERVICE_SETTINGS as const,
            id: `${client_id}:ALL`,
          },
        ];
        if (!response) return base;

        return [
          ...base,
          ...getProvidesTags<IServiceLvlItem>(
            response.data,
            EQueryTagTypes.ST_SERVICE_SETTINGS,
          ),
        ];
      },
    }),
    getAccountLvlSettings: build.query<
      IAccountLvlSettingsResponse,
      IAccountLvlSettingsQuery & {
        client_id?: number | string;
        service: string;
      }
    >({
      query: ({ client_id, service, page = 1, per_page = 15 }) => ({
        url: `api/service-tools/clients/${client_id}/advertise-services/${service}/accounts/settings`,
        method: "GET",
        params: { page, per_page },
      }),
      providesTags: (response, _err, { client_id, service }) => {
        const base = [
          {
            type: EQueryTagTypes.ST_ACCOUNT_SETTINGS as const,
            id: `${client_id}:${service}:LIST`,
          },
          {
            type: EQueryTagTypes.ST_ACCOUNT_SETTINGS as const,
            id: `${client_id}:ALL`,
          },
        ];
        if (!response) return base;

        return [
          ...base,
          ...getProvidesTags<{ id: number }>(
            (response.data as unknown as Array<{ id: number }>) ?? [],
            EQueryTagTypes.ST_ACCOUNT_SETTINGS,
          ),
        ];
      },
    }),
    updateAppLvlSettings: build.mutation<
      unknown,
      { client_id: number | string; data: unknown }
    >({
      query: ({ client_id, data }) => ({
        url: `api/service-tools/clients/${client_id}/settings`,
        method: "PATCH",
        body: { data },
      }),
      invalidatesTags: (_res, _err, { client_id }) => [
        {
          type: EQueryTagTypes.ST_APP_SETTINGS as const,
          id: `${client_id}:LIST`,
        },
        {
          type: EQueryTagTypes.ST_SERVICE_SETTINGS as const,
          id: `${client_id}:ALL`,
        },
        {
          type: EQueryTagTypes.ST_ACCOUNT_SETTINGS as const,
          id: `${client_id}:ALL`,
        },
      ],
    }),
    updateServiceLvlSettings: build.mutation<
      unknown,
      { client_id: number | string; service: string; data: unknown }
    >({
      query: ({ client_id, service, data }) => ({
        url: `api/service-tools/clients/${client_id}/advertise-services/${service}/settings`,
        method: "PATCH",
        body: { data },
      }),
      invalidatesTags: (_res, _err, { client_id, service }) => [
        {
          type: EQueryTagTypes.ST_SERVICE_SETTINGS as const,
          id: `${client_id}:${service}:LIST`,
        },
        {
          type: EQueryTagTypes.ST_ACCOUNT_SETTINGS as const,
          id: `${client_id}:${service}:LIST`,
        },
      ],
    }),
    updateAccountLvlSettings: build.mutation<
      unknown,
      { client_id: number | string; service: string; data: unknown }
    >({
      query: ({ client_id, service, data }) => ({
        url: `api/service-tools/clients/${client_id}/advertise-services/${service}/accounts/settings`,
        method: "PATCH",
        body: { data },
      }),
      invalidatesTags: (_res, _err, { client_id, service }) => [
        {
          type: EQueryTagTypes.ST_ACCOUNT_SETTINGS as const,
          id: `${client_id}:${service}:LIST`,
        },
      ],
    }),
    checkLaunchedProcesses: build.query({
      query: () => ({
        url: `/api/service-tools/settings/check-launched-processes`,
        method: "GET",
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetAppLvlSettingsQuery,
  useGetServiceLvlSettingsQuery,
  useGetAccountLvlSettingsQuery,
  useUpdateAppLvlSettingsMutation,
  useUpdateServiceLvlSettingsMutation,
  useUpdateAccountLvlSettingsMutation,
  useCheckLaunchedProcessesQuery,
} = serviceToolsClientApi;
